import {
  BarChart3,
  Briefcase,
  Building,
  Building2,
  Calendar,
  CalendarDays,
  DollarSign,
  FileSignature,
  FileText,
  GitFork,
  Layers,
  LayoutDashboard,
  Mail,
  Settings,
  Shield,
  ShieldCheck,
  Sparkles,
  TicketCheck,
  UserCheck,
  Users,
} from 'lucide-react';
import type { TabConfig } from '../components/BottomNav';
import type { AdminTabId } from './adminTabs';

/**
 * A sidebar entry whose id is a canonical admin tab id. Building the
 * structure as AdminSidebarTabConfig[] makes a sidebar id that is not
 * in ADMIN_TAB_IDS (lib/adminTabs) a compile error — the sidebar
 * consumes the canonical list (ADM-07) instead of re-declaring ids.
 */
type AdminSidebarTabConfig = Omit<TabConfig, 'id'> & { id: AdminTabId };

/**
 * Admin sidebar structure — owner-approved redesign (Oct 18, 2026).
 *
 * The admin menu used to be one long flat list of 25 entries grouped only
 * by printed category headers. The approved structure:
 *  - a short FLAT block of everyday pages,
 *  - one JOBS entry whose page carries a tab bar (Job List / Offers /
 *    Invitations — see adminJobsTabs.ts; the 'offers' and 'invitations'
 *    views keep their existing adminTab ids and deep links),
 *  - three collapsible groups (Templates / Organization / Orchestration),
 *  - account-level entries pinned at the bottom.
 *
 * Every tab id that existed before the redesign is present exactly once
 * here (or, for offers/invitations, in the Jobs page tab bar) — the
 * completeness suite in tests/adminNavStructure.test.ts pins that census
 * so no page can silently fall out of the navigation again.
 *
 * Module-access filtering stays in App.tsx (hasModuleAccess), applied to
 * this list exactly as it was applied to the old inline list.
 */
export function getAdminSidebarTabs(isPlatformAdmin: boolean): TabConfig[] {
  const tabs: AdminSidebarTabConfig[] = [
    // ── Flat: everyday pages ──────────────────────────────────────────
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, category: 'Main' },
    { id: 'applicants', label: 'Applicants', icon: Users, category: 'Main' },
    { id: 'approvals', label: 'Pending Approvals', icon: ShieldCheck, category: 'Main' },
    { id: 'documents', label: 'Documents', icon: FileText, category: 'Main' },
    { id: 'signing', label: 'E-Signatures', icon: FileSignature, category: 'Main' },
    { id: 'tickets', label: 'Support Tickets', icon: TicketCheck, category: 'Main' },
    { id: 'finance', label: 'Finance', icon: DollarSign, category: 'Main' },
    { id: 'reports', label: 'Reports & Analytics', icon: BarChart3, category: 'Main' },
    { id: 'my-events', label: 'My Events', icon: Calendar, category: 'Main' },
    { id: 'events', label: 'Events Calendar', icon: CalendarDays, category: 'Main' },

    // ── Jobs: one entry; its page carries the Job List / Offers /
    //    Invitations tab bar ──────────────────────────────────────────
    { id: 'jobs', label: 'Jobs', icon: Briefcase, category: 'Main' },

    // ── Templates (collapsible group) ────────────────────────────────
    { id: 'templates', label: 'Email Templates', icon: Mail, group: 'Templates' },
    { id: 'doc-templates', label: 'Doc Templates', icon: FileText, group: 'Templates' },

    // ── Organization (collapsible group) ─────────────────────────────
    { id: 'organizations', label: 'Organizations', icon: Building2, group: 'Organization' },
    { id: 'referrers', label: 'Referrers', icon: UserCheck, group: 'Organization' },
    { id: 'sponsors', label: 'Sponsors', icon: Shield, group: 'Organization' },
    { id: 'employers', label: 'Employers', icon: Building, group: 'Organization' },

    // ── Orchestration (collapsible group) ────────────────────────────
    { id: 'workflow', label: 'Workflow', icon: GitFork, group: 'Orchestration' },
    { id: 'automations', label: 'Automation', icon: Sparkles, group: 'Orchestration' },
    { id: 'batch', label: 'Batch Center', icon: Layers, group: 'Orchestration' },
    { id: 'ai-suggestions', label: 'AI Suggestions', icon: Sparkles, group: 'Orchestration' },

    // ── Bottom: account / workspace administration ───────────────────
    { id: 'users', label: 'Users & Access', icon: Users, category: 'Administration' },
    { id: 'settings', label: 'Workspace Settings', icon: Settings, category: 'Administration' },
    ...(isPlatformAdmin
      ? [{ id: 'workspaces', label: 'Workspaces', icon: Building2, category: 'Administration' } as AdminSidebarTabConfig]
      : []),
  ];
  return tabs;
}

/** Collapsible admin sidebar groups, in display order. */
export const ADMIN_NAV_GROUPS: Record<string, string[]> = {
  Templates: ['templates', 'doc-templates'],
  // Organization Special Requests surfaces (monitoring + templates) live
  // INSIDE the 'organizations' tab behind an in-page tab bar — the
  // sidebar census is owner-frozen, so they take no new sidebar id.
  Organization: ['organizations', 'referrers', 'sponsors', 'employers'],
  Orchestration: ['workflow', 'automations', 'batch', 'ai-suggestions'],
};

/**
 * The adminTab ids that live on the Jobs page (tab bar). The sidebar
 * highlights its single 'jobs' entry while any of these is active, and a
 * member with access to ANY of them keeps the Jobs entry (see App.tsx).
 * Every member is a canonical ADMIN_TAB_IDS id — the parity census in
 * tests/adminNavStructure.test.ts pins that (kept as plain strings
 * because App.tsx membership-tests raw URL tab values against it).
 */
export const JOBS_AREA_TAB_IDS = ['jobs', 'offers', 'invitations'];
